import { rollD6 } from '../utils/dice.js';
import { CONFIG, TASKS } from '../config/constants.js';
import { applyMoraleModifier } from './moraleSystem.js';
import { isOutdoorTask } from '../config/tasks.js';
import { getSeason } from '../utils/time.js';
import {
  calculateBuildingBonuses,
  hasPushInjuryImmunity,
  hasSpecialMechanic,
  getSpecialMechanicConfig
} from './taskEffectsSystem.js';

/**
 * Rolls dice for all assigned tasks
 * @param {Array} workers - Array of worker objects
 * @param {Object} assignments - Map of worker ID to task
 * @param {Object|null} tempWorker - Temporary worker from traveler event
 * @param {number} morale - Current morale (0-100)
 * @returns {Object} Map of task to roll results
 */
export function rollAllTasks(workers, assignments, tempWorker = null, morale = 50) {
  // Filter out dead, sick, and immature workers (immature children cannot work but still eat)
  const aliveWorkers = workers.filter(w => w.status !== 'dead' && w.status !== 'sick' && w.status !== 'immature');
  const allWorkers = tempWorker ? [...aliveWorkers, tempWorker] : aliveWorkers;

  const rolls = {};

  TASKS.forEach(task => {
    const workersOnTask = allWorkers.filter(w => assignments[w.id] === task);
    if (workersOnTask.length > 0 && task !== 'rest') {
      const results = workersOnTask.map(w => rollWorkerDie(w, task, morale));

      rolls[task] = {
        workers: workersOnTask.map(w => w.id),
        rolls: results.map(r => r.roll),
        caveIns: results.filter(r => r.caveIn).map((r, i) => workersOnTask[i].id) // Track cave-in victims
      };
    }
  });

  return rolls;
}

/**
 * Rolls a single die for a worker, applying injury and morale penalties/bonuses
 * @param {Object} worker - Worker object with status
 * @param {string} task - Task being performed (for cave-in detection)
 * @param {number} morale - Current morale (0-100)
 * @returns {Object} { roll, caveIn } - Roll result and cave-in status
 */
export function rollWorkerDie(worker, task = null, morale = 50) {
  const rawRoll = rollD6(); // Store natural d6 for cave-in check (before any modifiers)
  let roll = rawRoll;
  let caveIn = false;

  // Sick workers cannot work
  if (worker.status === 'sick') {
    return { roll: 0, caveIn: false };
  }

  // Apply injury penalty
  if (worker.status === 'injured') {
    roll = Math.max(1, roll - 2);
  }

  // Apply morale modifier
  roll = applyMoraleModifier(roll, morale);

  // Cave-in checks natural d6 only — injured workers' -2 penalty does NOT inflate cave-in risk
  if (hasSpecialMechanic(task, 'cave_in')) {
    const threshold = getSpecialMechanicConfig(task, 'caveInThreshold') || CONFIG.CAVE_IN_THRESHOLD;
    if (rawRoll <= threshold) {
      caveIn = true;
    }
  }

  return { roll, caveIn };
}

/**
 * Attempts to push a task for extra dice
 * @param {string} task - The task being pushed
 * @param {Array} buildings - Completed buildings (for immunity check)
 * @returns {Object} Push result with value and success status
 */
export function pushTask(task, buildings = []) {
  const pushRoll = rollD6();

  // Check for push injury immunity (e.g., barracks + guard)
  const immune = hasPushInjuryImmunity(task, buildings);
  const success = immune || pushRoll > CONFIG.PUSH_INJURY_THRESHOLD;

  return {
    value: pushRoll,
    success,
    task,
    immune  // Track if immunity applied
  };
}

/**
 * Calculates the total output for a task including modifiers
 * @param {Object} params - Parameters object
 * @param {string} params.task - The task type
 * @param {Object} params.rollData - Roll data with workers and rolls
 * @param {Object} params.pushes - Map of task to push results
 * @param {Array} params.buildings - Completed buildings
 * @param {Object|null} params.event - Current event
 * @param {string|null} params.bountyTask - Task receiving bounty bonus
 * @returns {number} Total task output
 */
export function calculateTaskTotal({ task, rollData, pushes = {}, buildings = [], event = null, bountyTask = null, day = null, tempBonuses = null }) {
  // Base total from rolls
  let total = rollData.rolls.reduce((a, b) => a + b, 0);

  // Add push if successful
  if (pushes[task]?.success) {
    total += pushes[task].value;
  }

  // Apply building bonuses using centralized system
  const buildingBonus = calculateBuildingBonuses(task, buildings, {
    workerCount: rollData.rolls.length,
    rollCount: rollData.rolls.length
  });
  total += buildingBonus;

  // Apply weather bonus (+2 to all tasks)
  if (event?.type === 'weather') {
    total += 2;
  }

  // Golden age bonus (+3 to all tasks today)
  if (event?.type === 'golden_age') {
    total += 3;
  }

  // Temporary farm bonus from story events (e.g. Blessing of Harvest)
  if (task === 'farm' && tempBonuses?.farmBonus) {
    const fb = tempBonuses.farmBonus;
    if (!fb.endsOn || day === null || day <= fb.endsOn) {
      total += fb.amount;
    }
  }

  // Winter farm penalty: -1 per die (min 0)
  if (task === 'farm' && day !== null && getSeason(day) === 'Winter') {
    total = Math.max(0, total - rollData.rolls.length);
  }

  // Apply bounty (double output)
  if (bountyTask === task) {
    total *= 2;
  }

  // Storm cancels outdoor tasks
  if (event?.type === 'storm' && isOutdoorTask(task)) {
    return 0;
  }

  return total;
}
